import { DateWithSession, Session } from '@eventbrite/checkout-fairy-api';
import { isBefore } from 'date-fns';
import { getSeriesInfiniteCalendarDiscountFF } from '../selectors/app';
import { getEventDates } from '../selectors/event/getEventDates';
import { CheckoutState } from '../state';
import { Discount } from '../state/infiniteCalendarSeries';

export type GetDateWithSession = {
    date: DateWithSession;
    session: Session;
};

const searchForSession = (
    date: DateWithSession,
    id: string,
    cancelToken: any,
) =>
    new Promise((resolve: any, reject: any) => {
        const sessions = date.sessions;
        for (let index = 0; index < sessions.length; index++) {
            if (cancelToken.cancelled) {
                reject(new Error('Canceled search for session'));
            }

            const session = sessions[index];
            if (session.id === id) {
                cancelToken.cancelled = true;
                resolve({
                    date,
                    session,
                });
            }
        }

        reject(new Error('Not Found'));
    });

export const getDateWithSession = async (
    eventDates: ReturnType<typeof getEventDates>,
    childEventId: string,
): Promise<any> => {
    if (!eventDates || Object.keys(eventDates).length === 0) {
        return;
    }
    const cancelToken = { cancelled: false };
    const firstMonthWithSessions = Object.keys(eventDates)[0];
    const dates = eventDates[firstMonthWithSessions];
    const searches = dates.map((date) =>
        searchForSession(date, childEventId, cancelToken),
    );
    const searchResults = await Promise.allSettled(searches);

    const fulfilledResult = searchResults.find(
        (result): result is PromiseFulfilledResult<typeof getEventDates> =>
            result.status === 'fulfilled',
    );

    return fulfilledResult?.value;
};

export const getDateWithSessionStartDatetime = (
    eventDates: ReturnType<typeof getEventDates>,
    startDatetime: string,
): { date: DateWithSession; session: Session } | undefined => {
    if (!eventDates || !startDatetime) {
        return;
    }

    const sessionStartDatetime = startDatetime.substring(
        0,
        startDatetime.indexOf('T'),
    );
    const firstMonthWithSessions = Object.keys(eventDates)[0];

    if (!firstMonthWithSessions) {
        return;
    }

    const dates = eventDates[firstMonthWithSessions];
    const foundDate = dates.find((date) => date.date === sessionStartDatetime);
    if (!foundDate) {
        return;
    }

    const foundSession = foundDate.sessions.find(
        (session) => session.startDatetime === startDatetime,
    );

    if (!foundSession) {
        return;
    }

    return {
        date: foundDate,
        session: foundSession,
    };
};

export const getCurrentOrFutureMonth = (utcDate: string) => {
    const inputDate = new Date(utcDate);
    const today = new Date();
    const referenceDate = isBefore(inputDate, today) ? today : inputDate;
    return {
        month: referenceDate.getMonth(),
        year: referenceDate.getFullYear(),
    };
};

export const isFirstTwoMonths = (
    month: number,
    year: number,
    referenceDate: { month: number; year: number },
): boolean => {
    const referenceMonth = referenceDate.month;
    const referenceYear = referenceDate.year;

    if (referenceMonth === 11) {
        return (
            (month === 0 && year === referenceYear + 1) ||
            (month === 11 && year === referenceYear)
        );
    }

    return month <= referenceMonth + 1 && year <= referenceYear;
};

export const removeDisabledDiscounts = <T extends { discount?: Discount }>(
    items: T[],
    state: CheckoutState,
): T[] => {
    return getSeriesInfiniteCalendarDiscountFF(state)
        ? items
        : items.map(({ discount: _discount, ...rest }) => rest as T);
};
